import { useState } from 'react'
import { AlertTriangle, CircleSlash, FileDown, FileText, ShieldCheck } from 'lucide-react'
import type {
  GutachtenAbschnittStatus,
  GutachtenRequest,
  GutachtenResponse,
  GutachtenVorlageValue,
  HaftungZuordnungValue,
} from '@mindkeep/shared'
import { useGutachtenErstellen, useGutachtenVorlagen } from '@/features/gutachten/use-gutachten'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { PageHeader } from '@/components/page-header'
import { ErrorState } from '@/components/error-state'
import { SkeletonList } from '@/components/skeleton-list'

/**
 * Gutachten-Ausgabevorlagen (RNA M7).
 *
 * Die Seite zeigt eine Regel, die im Backend steht und dort nicht verhandelbar
 * ist: ein Punkt ohne abgerufene Fundstelle bleibt leer und erscheint unter
 * «Offene Punkte». Deshalb tragen die Felder fuer Ursache und
 * Haftungszuordnung ein Fundstellenfeld direkt daneben — wer hier nichts
 * eintraegt, bekommt keinen Text, sondern eine sichtbare Luecke.
 *
 * Die Feststellung ist der einzige Abschnitt ohne Fundstellenfeld. Sie ist die
 * eigene Beobachtung der Fachperson.
 */

const STATUS_LABEL: Record<GutachtenAbschnittStatus, string> = {
  belegt: 'belegt',
  annahme: 'Annahme',
  leer: 'leer',
}

const ZUORDNUNG_OPTIONEN: Array<{ wert: HaftungZuordnungValue; label: string }> = [
  { wert: 'ausfuehrung', label: 'Ausfuehrung' },
  { wert: 'planung', label: 'Planung' },
  { wert: 'nutzung', label: 'Nutzung' },
  { wert: 'ungeklaert', label: 'ungeklaert' },
]

function StatusBadge({ status }: { status: GutachtenAbschnittStatus }) {
  if (status === 'belegt') {
    return (
      <Badge className="gap-1 bg-success text-success-foreground">
        <ShieldCheck className="h-3 w-3" />
        {STATUS_LABEL.belegt}
      </Badge>
    )
  }
  if (status === 'annahme') {
    return (
      <Badge className="gap-1 bg-warning text-warning-foreground">
        <AlertTriangle className="h-3 w-3" />
        {STATUS_LABEL.annahme}
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="gap-1 text-muted-foreground">
      <CircleSlash className="h-3 w-3" />
      {STATUS_LABEL.leer}
    </Badge>
  )
}

/** Zeilen eines Textfelds als Liste — leere Zeilen fallen weg. */
function zeilen(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

function kommaListe(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
}

function jahrOderNull(value: string): number | null {
  const jahr = Number.parseInt(value.trim(), 10)
  return Number.isInteger(jahr) && jahr >= 1800 && jahr <= 2200 ? jahr : null
}

export function GutachtenPage() {
  const vorlagen = useGutachtenVorlagen()
  const erstellen = useGutachtenErstellen()
  const [ergebnis, setErgebnis] = useState<GutachtenResponse | null>(null)

  const [vorlage, setVorlage] = useState<GutachtenVorlageValue>('vorstudie')
  const [bezeichnung, setBezeichnung] = useState('')
  const [fragestellung, setFragestellung] = useState('')
  const [ausfuehrungsjahr, setAusfuehrungsjahr] = useState('')
  const [sanierungsjahr, setSanierungsjahr] = useState('')
  const [rechtsraum, setRechtsraum] = useState<'ch' | 'fl' | ''>('')
  const [bauteil, setBauteil] = useState('')
  const [schadensbild, setSchadensbild] = useState('')
  const [normfamilien, setNormfamilien] = useState('')
  const [feststellungen, setFeststellungen] = useState('')
  const [ursacheText, setUrsacheText] = useState('')
  const [ursacheFundstellen, setUrsacheFundstellen] = useState('')
  const [ursacheAnnahme, setUrsacheAnnahme] = useState(false)
  const [zuordnung, setZuordnung] = useState<HaftungZuordnungValue>('ungeklaert')
  const [haftungText, setHaftungText] = useState('')
  const [haftungFundstellen, setHaftungFundstellen] = useState('')
  const [offenePunkte, setOffenePunkte] = useState('')

  const bereit = bezeichnung.trim().length > 0 && fragestellung.trim().length > 0

  function absenden() {
    const body: GutachtenRequest = {
      vorlage,
      fragestellung: fragestellung.trim(),
      objekt: {
        bezeichnung: bezeichnung.trim(),
        ausfuehrungsjahr: jahrOderNull(ausfuehrungsjahr),
        sanierungsjahr: jahrOderNull(sanierungsjahr),
        rechtsraum: rechtsraum === '' ? null : rechtsraum,
      },
      bauteil: bauteil.trim() || null,
      schadensbild: schadensbild.trim() || null,
      normfamilien: kommaListe(normfamilien),
      feststellungen: zeilen(feststellungen),
      ursache: ursacheText.trim()
        ? {
            text: ursacheText.trim(),
            fundstellen: zeilen(ursacheFundstellen),
            annahme: ursacheAnnahme,
          }
        : null,
      haftung: haftungText.trim()
        ? {
            zuordnung,
            text: haftungText.trim(),
            fundstellen: zeilen(haftungFundstellen),
            annahme: false,
          }
        : null,
      fotos: [],
      weitere_offene_punkte: zeilen(offenePunkte),
    }

    erstellen.mutate(body, { onSuccess: setErgebnis })
  }

  if (vorlagen.isLoading) return <SkeletonList rows={4} />
  if (vorlagen.isError) {
    return (
      <ErrorState
        title="Vorlagen konnten nicht geladen werden"
        description={vorlagen.error instanceof Error ? vorlagen.error.message : undefined}
        onRetry={() => void vorlagen.refetch()}
      />
    )
  }

  return (
    // R1: gemeinsamer Flow-Arbeitsrahmen — max 1180px, mobil 16px Rand.
    <div className="mx-auto w-full max-w-[1180px] px-4 py-6 min-[761px]:px-12 min-[761px]:py-8">
      <PageHeader
        title="Gutachten"
        description="Feststellung, Ursache, Normbezug, Haftungszuordnung, Offene Punkte. Was nicht aus einer freigegebenen Quelle belegt ist, bleibt leer — der Bericht erfindet nichts."
      />

      <Card className="mb-6">
        <CardContent className="space-y-4 pt-6">
          <div>
            <Label>Stufe</Label>
            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              {vorlagen.data?.vorlagen.map((spec) => (
                <button
                  key={spec.id}
                  type="button"
                  aria-pressed={vorlage === spec.id}
                  onClick={() => setVorlage(spec.id)}
                  className={`min-h-11 min-[761px]:min-h-9 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    vorlage === spec.id
                      ? 'border-primary bg-primary/5'
                      : 'border-input hover:bg-muted/50'
                  }`}
                >
                  <div className="text-sm font-medium text-foreground">{spec.titel}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{spec.zweck}</div>
                  {spec.belegtiefe === 'vollbeleg' && (
                    <div className="mt-2 flex items-start gap-1.5 rounded-md bg-warning px-2 py-1.5 text-xs font-medium text-warning-foreground">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span>Keine Annahmen — jede Aussage ist zu belegen.</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="objekt">Objekt</Label>
              <Input
                id="objekt"
                value={bezeichnung}
                onChange={(event) => setBezeichnung(event.target.value)}
                placeholder="Mehrfamilienhaus Musterweg"
              />
            </div>
            <div>
              <Label htmlFor="rechtsraum">Rechtsraum</Label>
              <select
                id="rechtsraum"
                value={rechtsraum}
                onChange={(event) => setRechtsraum(event.target.value as 'ch' | 'fl' | '')}
                className="flex h-11 min-[761px]:h-9 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-[16px] min-[761px]:text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">nicht bestimmt</option>
                <option value="ch">CH (Schweiz)</option>
                <option value="fl">FL (Fürstentum Liechtenstein)</option>
              </select>
              <p className="mt-1 text-xs text-muted-foreground">
                Ohne Rechtsraum bleibt die Haftungszuordnung leer.
              </p>
            </div>
            <div>
              <Label htmlFor="ausfuehrungsjahr">Ausführungsjahr</Label>
              <Input
                id="ausfuehrungsjahr"
                value={ausfuehrungsjahr}
                onChange={(event) => setAusfuehrungsjahr(event.target.value)}
                placeholder="1998"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Bestimmt die Fassung. Ohne Jahr lautet der Normbezug «Generation nicht bestimmbar».
              </p>
            </div>
            <div>
              <Label htmlFor="sanierungsjahr">Sanierungsjahr (optional)</Label>
              <Input
                id="sanierungsjahr"
                value={sanierungsjahr}
                onChange={(event) => setSanierungsjahr(event.target.value)}
                placeholder="2012"
              />
            </div>
            <div>
              <Label htmlFor="bauteil">Bauteil</Label>
              <Input
                id="bauteil"
                value={bauteil}
                onChange={(event) => setBauteil(event.target.value)}
                placeholder="Flachdach"
              />
            </div>
            <div>
              <Label htmlFor="schadensbild">Schadensbild</Label>
              <Input
                id="schadensbild"
                value={schadensbild}
                onChange={(event) => setSchadensbild(event.target.value)}
                placeholder="Durchfeuchtung"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="fragestellung">Fragestellung</Label>
            <Textarea
              id="fragestellung"
              rows={2}
              value={fragestellung}
              onChange={(event) => setFragestellung(event.target.value)}
              placeholder="Woher stammt die Durchfeuchtung am Deckenanschluss?"
            />
          </div>

          <div>
            <Label htmlFor="normfamilien">Normfamilien (mit Komma getrennt)</Label>
            <Input
              id="normfamilien"
              value={normfamilien}
              onChange={(event) => setNormfamilien(event.target.value)}
              placeholder="SIA 271, SIA 118"
            />
          </div>

          <div>
            <Label htmlFor="feststellungen">Feststellung — je Zeile eine Beobachtung</Label>
            <Textarea
              id="feststellungen"
              rows={3}
              value={feststellungen}
              onChange={(event) => setFeststellungen(event.target.value)}
              placeholder="Am Deckenanschluss zeigt sich eine Durchfeuchtung über die ganze Breite."
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Eigene Beobachtung, ohne Wertung. Dieser Abschnitt braucht keine Fundstelle.
            </p>
          </div>

          <div className="rounded-lg border border-input p-4">
            <Label htmlFor="ursache">Ursache — technische Herleitung</Label>
            <Textarea
              id="ursache"
              rows={3}
              value={ursacheText}
              onChange={(event) => setUrsacheText(event.target.value)}
              placeholder="Der Anschluss der Abdichtung an das aufgehende Bauteil ist unzureichend ausgeführt."
            />
            <Label htmlFor="ursache-fundstellen" className="mt-3 block">
              Fundstellen — je Zeile ein Vault-Pfad
            </Label>
            <Textarea
              id="ursache-fundstellen"
              rows={2}
              value={ursacheFundstellen}
              onChange={(event) => setUrsacheFundstellen(event.target.value)}
              placeholder="_Knowledge/bau-normen/wiki/SB_Flachdach_Durchfeuchtung.md"
            />
            <label className="mt-3 flex min-h-11 min-[761px]:min-h-9 items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="h-5 w-5 shrink-0 accent-primary"
                checked={ursacheAnnahme}
                onChange={(event) => setUrsacheAnnahme(event.target.checked)}
              />
              Als Annahme kennzeichnen (ohne Quellenangabe, im Endbericht nicht zulässig)
            </label>
          </div>

          <div className="rounded-lg border border-input p-4">
            <Label htmlFor="zuordnung">Haftungszuordnung</Label>
            <select
              id="zuordnung"
              value={zuordnung}
              onChange={(event) => setZuordnung(event.target.value as HaftungZuordnungValue)}
              className="flex h-11 min-[761px]:h-9 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-[16px] min-[761px]:text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {ZUORDNUNG_OPTIONEN.map((option) => (
                <option key={option.wert} value={option.wert}>
                  {option.label}
                </option>
              ))}
            </select>
            <Label htmlFor="haftung" className="mt-3 block">
              Begründung
            </Label>
            <Textarea
              id="haftung"
              rows={2}
              value={haftungText}
              onChange={(event) => setHaftungText(event.target.value)}
            />
            <Label htmlFor="haftung-fundstellen" className="mt-3 block">
              Fundstellen — je Zeile ein Vault-Pfad
            </Label>
            <Textarea
              id="haftung-fundstellen"
              rows={2}
              value={haftungFundstellen}
              onChange={(event) => setHaftungFundstellen(event.target.value)}
            />
          </div>

          <div>
            <Label htmlFor="offene-punkte">Weitere offene Punkte — je Zeile einer</Label>
            <Textarea
              id="offene-punkte"
              rows={2}
              value={offenePunkte}
              onChange={(event) => setOffenePunkte(event.target.value)}
            />
          </div>

          <Button onClick={absenden} disabled={!bereit || erstellen.isPending} className="w-full min-[761px]:w-auto">
            {erstellen.isPending ? 'Erstelle Gutachten…' : 'Gutachten erstellen'}
          </Button>
        </CardContent>
      </Card>

      {ergebnis && <Ergebnis ergebnis={ergebnis} />}
    </div>
  )
}

function Ergebnis({ ergebnis }: { ergebnis: GutachtenResponse }) {
  return (
    // R1: Ergebnis in Lesebreite 720px.
    <div className="mx-auto w-full max-w-[720px] space-y-4">
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div>
            <h2 className="text-[16px] font-[650] tracking-tight text-foreground">{ergebnis.titel}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {ergebnis.beleglage.abgerufen} Passage(n) abgerufen, {ergebnis.beleglage.verwendbar}{' '}
              verwendbar, {ergebnis.beleglage.nicht_verwendbar} nicht verwendbar.
            </p>
          </div>

          <div className="space-y-2">
            {ergebnis.abschnitte.map((abschnitt) => (
              <div
                key={abschnitt.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-input px-3 py-2"
              >
                <span className="min-w-0 break-words text-sm text-foreground">{abschnitt.titel}</span>
                <StatusBadge status={abschnitt.status} />
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={ergebnis.artefakte.markdown.storage_url} target="_blank" rel="noreferrer">
                <FileText className="mr-2 h-4 w-4" />
                Markdown
              </a>
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href={ergebnis.artefakte.docx.storage_url} target="_blank" rel="noreferrer">
                <FileDown className="mr-2 h-4 w-4" />
                Word (.docx)
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {ergebnis.offene_punkte.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-[16px] font-[650] tracking-tight text-foreground">Offene Punkte</h3>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {ergebnis.offene_punkte.map((punkt, index) => (
                <li key={`${punkt.abschnitt ?? 'allgemein'}-${index}`} className="break-words">· {punkt.text}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          <h3 className="text-[16px] font-[650] tracking-tight text-foreground">Fundstellenliste</h3>
          {ergebnis.fundstellen.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Keine Fundstelle verwendet.</p>
          ) : (
            <ul className="mt-2 space-y-2 text-sm">
              {ergebnis.fundstellen.map((fundstelle) => (
                <li key={fundstelle.path} className="min-w-0">
                  <span className="break-all font-mono text-xs text-muted-foreground">{fundstelle.path}</span>
                  <div className="break-words text-foreground">{fundstelle.label}</div>
                  {!fundstelle.verwendbar && (
                    <div className="mt-1 flex items-start gap-1.5 break-words rounded-md bg-warning px-2 py-1.5 text-xs text-warning-foreground">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      <span>Nicht verwendbar: {fundstelle.vorbehalt}</span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="text-[16px] font-[650] tracking-tight text-foreground">Bericht</h3>
          <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-3 font-mono text-xs text-foreground">
            {ergebnis.markdown}
          </pre>
        </CardContent>
      </Card>
    </div>
  )
}
